import { useEffect, useMemo, useRef, useState } from "react"
import { Check } from "lucide-react"
import {
    Command,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/Components/ui/command"
import { Button } from "@/Components/ui/button"
import { useTranslation } from "react-i18next"
import { useQuery } from "@tanstack/react-query"
import { makeRequest } from "@/helpers/apiWrapper"
import iso2 from "../../Utils/countries_iso2.json"

type Item = {
    id?: string
    name: string
    code?: string
}

const getCities = async (iso2: string) => {
    const res = await makeRequest({
        endpoint: `/api/country/get-cities/${iso2}`,
        server: "main",
    })
    return res?.data || []
} 

const getStates = async (iso2: string) => {
    const res = await makeRequest({
        endpoint: `/api/country/get-states/${iso2}`,
        server: "main",
    })
    return res?.data || []
}

const CitiesDropDown = ({
    value,
    setValue,
    disabled = false,
    iso_3 = "",
    isState = false,
    title = "Select City",
}) => {
    const { t } = useTranslation()
    const containerRef = useRef<HTMLDivElement>(null)
    const [isSearching, setIsSearching] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")

    const iso2Value = iso_3 ? iso2[iso_3]?.toUpperCase() : ""

    const { data: cities = [] } = useQuery({
        queryKey: ["GetCities", iso2Value],
        queryFn: () => getCities(iso2Value),
        enabled: !!iso2Value,
        staleTime: Infinity,
    })

    const { data: states = [] } = useQuery({
        queryKey: ["GetStates", iso2Value],
        queryFn: () => getStates(iso2Value),
        enabled: !!iso2Value,
        staleTime: Infinity,
    })

    const rawItems: Item[] = isState ? states : cities

    const uniqueItems = useMemo(() => {
        const map = new Map<string, Item>()
        rawItems.forEach(item => {
            map.set(item.name.toLowerCase(), item)
        })
        return Array.from(map.values())
    }, [rawItems])

    const filteredItems = useMemo(() => {
        if (!searchTerm) return uniqueItems
        const term = searchTerm.toLowerCase()
        return uniqueItems.filter(item =>
            item.name.toLowerCase().includes(term)
        )
    }, [searchTerm, uniqueItems])
  
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setIsSearching(false)
                setSearchTerm("")
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    return (
        <div
            ref={containerRef}
            className="relative w-full"
            key={isState ? "state-dropdown" : "city-dropdown"}
        >
            {isSearching ? (
                <Command className="w-full">
                    <CommandInput
                        autoFocus
                        placeholder={`Search ${isState ? "State" : "City"}`}
                        value={searchTerm}
                        onValueChange={setSearchTerm}
                    />

                    <CommandList className="absolute top-full z-50 max-h-[250px] w-full overflow-auto rounded-lg bg-white shadow-lg">
                        <CommandGroup>
                            {filteredItems.length ? (
                                filteredItems.map(item => (
                                    <CommandItem
                                        key={`${iso2Value}-${item.name}`}
                                        value={`${item.name}`}
                                        onSelect={() => {
                                            setValue(item.name)
                                            setIsSearching(false)
                                            setSearchTerm("")
                                        }}
                                    >
                                        <span className="font-semibold">{item.name}</span>
                                        {value === item.name && (
                                            <Check className="ml-auto h-4 w-4" />
                                        )}
                                    </CommandItem>
                                ))
                            ) : (
                                <span className="block p-2 text-center text-muted-foreground">
                                    {t("No results found")}
                                </span>
                            )}
                        </CommandGroup>
                    </CommandList>
                </Command>
            ) : (
                <Button
                    disabled={disabled}
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => setIsSearching(true)}
                >
                    {value ? (
                        <span className="font-semibold">{value}</span>
                    ) : (
                        <span className="text-muted-foreground">{t(title)}</span>
                    )}
                </Button>
            )}
        </div>
    )
}

export default CitiesDropDown